import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { FormModel, Input, Select, Icon, Tooltip, Checkbox, Modal, Spin } from "ant-design-vue";
import { ContentService, CurrentContentDetailsForm } from "@/services/content";
import { cloneDeep, max, range } from "lodash";
import { CategoryResponseModel, TypeResponseModel } from "@/services/category-manager";
import { LanguageSkillModel, VCSLevelModel } from "@/models/title-list-model";
import CategoryManagerState from "@/modules/category-manager";
import { getModule } from "vuex-module-decorators";
import TitleListState from "@/modules/title-list";
import { fmtMsg, GLUtil, isGuid, MessageHelper } from "commonui";
import { VersionGroupModule } from "@/modules/versiongroup";
import { Paths } from "@/util/paths";
import { CommonFormLocale, TitleListLocale } from "@/locales/localeid";
import { AccessLevel, NonCurriculumComponentType, CurriculumType } from "@/util/enum";
import { ApplicationViews } from "@/util/enum";
import { disabledDueToAccessLevel, getRouteAccessLevel } from "@/util/func";
import { MediaSlotBinding } from "@/views/text-guardian/sub-views/title-list/sub-views/media/utils/const";

@Component({
	components: {
		Form: FormModel,
		FormItem: FormModel.Item,
		Input,
		Select,
		SelectOption: Select.Option,
		Icon,
		Tooltip,
		Checkbox,
		Spin
	}
})
export default class CommonForm extends Vue {
	@Prop() showComparison!: () => void;
	public CurrentDeails!: CurrentContentDetailsForm; // check in meta
	public versionGroupModule = VersionGroupModule;
	private categoryModule = getModule(CategoryManagerState);
	private titleListModule = getModule(TitleListState);

	public categories: CategoryResponseModel[] = []; // only pass categories which have types
	public skills: LanguageSkillModel[] = [];
	public levels: VCSLevelModel[] = []; // alias for Units
	public availableTypes: TypeResponseModel[] = [];
	public contentDetails: CurrentContentDetailsForm = {
		title: "",
		categoryId: 0,
		contentTypeId: 0,
		languageSkillId: 0,
		vcsLevelId: 0
	};
	public chapterName = "";
	public revisions: number[] = [];
	public contentDetailsProxy: CurrentContentDetailsForm = {} as CurrentContentDetailsForm;
	public fmtMsg = fmtMsg;
	public locale = TitleListLocale;
	public labelCol = { span: 8 };
	public wrapperCol = { span: 16 };
	public accessLevel = AccessLevel;
	public relatedLesson = false;
	public messRemoveConversation = fmtMsg(CommonFormLocale.CommonMessageIsParent);
	public messRemoveConversationInPlaylist = fmtMsg(CommonFormLocale.CommonMessageIsParentRelatedPlaylist);
	public isUnitBasedComponents: boolean = false;

	rules = {
		title: [{ required: true, message: fmtMsg(this.locale.TitleListCreateTitle), trigger: "blur" }]
	};

	@Watch("titleListModule.editedContentId")
	updateLevelsAfterCreation() {
		if (this.titleListModule.editedContentId.length) {
			const currentLevel = this.levels.find((l) => l.id === this.titleListModule.editedContentDetails.vcsLevelId);
			this.levels = this.levels.filter((l) => l.curriculumType === currentLevel?.curriculumType);
		}
	}

	@Watch("contentDetailsProxy.categoryId")
	@Watch("contentDetailsProxy.contentTypeId")
	updateTypes() {
		this.resetREPCM();
	}

	@Watch("titleListModule.currentRevision")
	updateRevisionList() {
		if (this.titleListModule.currentRevision > max(this.revisions)!) {
			this.revisions = range(0, this.titleListModule.currentRevision + 1);
		}
	}

	@Watch("versionGroupModule.selectedGsVersion")
	@Watch("versionGroupModule.selectedLsVersion")
	versionChanged() {
		this.updateLevels();
		if (this.titleListModule.editedContentId.length) {
			if (
				(this.titleListModule.editedContentDetails.curriculumType === CurriculumType.GrapeSEED &&
					!isGuid(VersionGroupModule.selectedGsVersion)) ||
				(this.titleListModule.editedContentDetails.curriculumType === CurriculumType.LittleSEED &&
					!isGuid(VersionGroupModule.selectedLsVersion))
			) {
				this.$router.replace(Paths.NonCurriculumComponents);
				this.titleListModule.resetChildState();
			}
		}
	}

	get isTextOrManageTab() {
		const currentPath = this.$route.matched.slice(-1)[0].path;
		if (currentPath === Paths.NonCurriculumComponentsEditText || currentPath === Paths.NonCurriculumComponentsText) {
			return true;
		}
		return false;
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	resetREPCM() {
		const category = this.categoryModule.categories.find((c) => c.id === this.contentDetailsProxy.categoryId);

		if (category) {
			const subCategory = category.types.find((t) => t.id === this.contentDetailsProxy.contentTypeId);
			this.titleListModule.setAllowTeachingPlan(category.allowTeachingPlan);

			if (subCategory) {
				this.titleListModule.setAllowRepPlan(subCategory.allowRepPlan);
				this.titleListModule.setIsSubCategorySupportResource(subCategory.isSupportResource);
			}
		}

		if (
			!this.titleListModule.allowTeachingPlan &&
			[ApplicationViews.ContentObjective, ApplicationViews.ContentExposure].includes(this.$route.meta.viewId)
		) {
			this.$router.replace(GLUtil.pathStringify(Paths.NonCurriculumComponentsEditText, { id: this.titleListModule.editedContentId }));
		}
	}

	handleUnitChange(value) {
		const contentDetails = { ...this.titleListModule.editedContentDetails, vcsLevelId: value };
		this.titleListModule.setContentDetails(contentDetails);
	}

	updateLevels() {
		let levels = this.titleListModule.levels;
		// Filter levels according to selected versions
		if (!isGuid(VersionGroupModule.selectedGsVersion)) {
			levels = levels.filter((l) => l.curriculumType !== CurriculumType.GrapeSEED);
		}
		if (!isGuid(VersionGroupModule.selectedLsVersion)) {
			levels = levels.filter((l) => l.curriculumType !== CurriculumType.LittleSEED);
		}
		if (!levels.length) {
			this.$router.push(Paths.NonCurriculumComponents);
			return;
		}
		this.levels = levels;
		// if not in edit mode, then update the selected level as in this.titleListModule.listSelectedLevel
		if (this.titleListModule.editedContentId.length === 0 && this.levels.length) {
			if (this.titleListModule.listSelectedLevel === 0) {
				this.contentDetailsProxy.vcsLevelId = this.levels[0].id;
			} else {
				const selectedLevel = this.levels.find((l) => l.id === this.titleListModule.listSelectedLevel)?.id;
				if (typeof selectedLevel === "number") {
					this.contentDetailsProxy.vcsLevelId = selectedLevel as number;
				} else {
					this.contentDetailsProxy.vcsLevelId = this.levels[0].id;
				}
			}
		}
	}

	mounted() {
		const currentPath = this.$route.matched.slice(-1)[0].path;
		let isCurriculumComponentsPage = true;
		if (currentPath === Paths.NonCurriculumComponentsText || currentPath === Paths.NonCurriculumComponentsEditText) {
			isCurriculumComponentsPage = false;
		}
		this.categories = this.categoryModule.categories.filter(
			(cat) => cat.types.length && !cat.disabled && !cat.isLessonManagement
		);

		if (isCurriculumComponentsPage) {
			this.availableTypes = this.categories[0].types.filter((type) => !type.disabled);
			this.skills = this.titleListModule.languageSkills;
			this.updateLevels();
			this.updateContentDetails(); // create form object model
		}
		else {
			this.isUnitBasedComponents = this.titleListModule.isunitBased === NonCurriculumComponentType.Unit;
			this.categories = this.categories.filter(c => c.isCurriculum === isCurriculumComponentsPage && c.isUnitBased === this.isUnitBasedComponents);
			this.availableTypes = this.categories[0].types.filter((type) => !type.disabled);
			this.skills = this.titleListModule.languageSkills;
			this.updateLevels();
			this.updateContentDetails(); // create form object model
		}
	}

	beforeDestroy() {
		this.titleListModule.setValidForm(false);
		this.titleListModule.setFormTocuched(false);
	}

	async updateContentDetails() {
		while (this.titleListModule.loading) {
			await new Promise((res) => setTimeout(res, 100));
		}
		if (
			Object.keys(this.titleListModule.editedContentDetails).length &&
			this.titleListModule.editedContentId.length
		) {
			const conCopy = cloneDeep(this.titleListModule.editedContentDetails);
			const category = this.categoryModule.categories.find((c) =>
				c.types.some((c) => c.id === conCopy.contentTypeId)
			) as CategoryResponseModel;
			const currentLevel = this.levels.find((l) => l.id === conCopy.vcsLevelId);
			this.levels = this.levels.filter((l) => l.curriculumType === currentLevel?.curriculumType);
			this.availableTypes = category.types.filter((t) => !t.disabled);
			// update contentDetails from conCopy
			this.contentDetails = {
				title: conCopy.title,
				categoryId: category.id,
				contentTypeId: conCopy.contentTypeId,
				languageSkillId: conCopy.languageSkillId,
				vcsLevelId: conCopy.vcsLevelId
			};
			// set the appropriate revision range
			if (conCopy.chapters.length) {
				const selChap = conCopy.chapters
					.find((c) => c.id === this.titleListModule.editeContentSelectedChapterId)
					?.contentText.map((m) => m.revision);
				if (selChap) {
					this.titleListModule.setOnlyIsParent(true);
					this.revisions = range(0, (max(selChap) as number) + 1);
				} else {
					this.revisions = [0];
					if (!this.titleListModule.currentRevision) {
						this.titleListModule.setRevision(0);
					}
				}
			} else {
				this.revisions = range(0, conCopy.revisions + 1);
			}

			this.titleListModule.setAllowTeachingPlan(category.allowTeachingPlan);
			const subCategory = category.types.find((t) => t.id === conCopy.contentTypeId);

			if (subCategory) {
				this.titleListModule.setAllowRepPlan(subCategory.allowRepPlan);
			}

			this.titleListModule.setREPCM({
				category: this.contentDetails.categoryId,
				contentTypeId: conCopy.contentTypeId
			});
			this.titleListModule.setUpdatedDetails(this.contentDetails);
			this.createProxy();
		} else if (!this.titleListModule.editedContentId.length) {
			const category = this.categories[0];

			this.contentDetails = {
				title: "",
				categoryId: category.id,
				contentTypeId: category.types[0].id,
				languageSkillId: this.skills[0].id,
				vcsLevelId:
					this.titleListModule.listSelectedLevel != 0
						? this.titleListModule.listSelectedLevel
						: this.levels[0].id
			};
			this.revisions = [0]; // for new title set the revision manually to zero
			if (this.titleListModule.currentRevision !== 0) {
				this.titleListModule.setRevision(0);
			}

			this.titleListModule.setAllowTeachingPlan(category.allowTeachingPlan);
			const subCategory = category.types.find((t) => t.id === this.contentDetails.contentTypeId);

			if (subCategory) {
				this.titleListModule.setAllowRepPlan(subCategory.allowRepPlan);
			}

			this.titleListModule.setREPCM({
				category: this.contentDetails.categoryId,
				contentTypeId: this.categories[0].types[0].id
			});
			this.titleListModule.setUpdatedDetails(this.contentDetails);
			this.createProxy();
		}
	}

	createProxy() {
		// this will help in capturing the change
		this.contentDetailsProxy = new Proxy(this.contentDetails, {
			set: (target, key, value) => {
				if (key === "categoryId") {
					// when category value is being changed, update the types (sub-categories)
					const category = this.categories.find((c) => c.id === value) as CategoryResponseModel;
					this.availableTypes = category.types.filter((t) => !t.disabled);
					this.contentDetailsProxy.contentTypeId = category.types[0].id;
				}

				this.titleListModule.setFormTocuched(true);
				target[key] = value;
				this.titleListModule.setUpdatedDetails(this.contentDetails);
				return true;
			}
		});
	}

	get hideComparer() {
		const { editedContentId, editedContentDetails, isParent } = this.titleListModule;
		if (
			!editedContentId.length ||
			(isParent && (!editedContentDetails.chapters || !editedContentDetails.chapters.length))
		) {
			return true;
		}
		return false;
	}

	revisionChanged(rev: number) {
		const {
			setRevision,
			setRevisionChanged,
			fetchContentDetails,
			togglePageDirty,
			textPageDirty,
			editeContentSelectedChapterId
		} = this.titleListModule;

		const onLeave = () => {
			setRevision(rev);
			if (editeContentSelectedChapterId !== "none" && editeContentSelectedChapterId !== "all") {
				// chapter revision is changed, to re-render the textarea
				setRevisionChanged(true);
			} else {
				fetchContentDetails().then(() => setTimeout(() => setRevisionChanged(true), 250));
			}
		};

		if (textPageDirty) {
			MessageHelper.Confirm(fmtMsg(TitleListLocale.TitleListLeaveWarn), () => {
				togglePageDirty();
				onLeave();
			});
		} else {
			onLeave();
		}
	}

	formValidated(rule: string, passed: boolean) {
		this.titleListModule.setValidForm(passed);
		this.titleListModule.setFormTocuched(true);
	}

	getFormDetails(): Promise<boolean | object> {
		const form = this.$refs.commonForm as FormModel;
		return new Promise((res) => {
			form.validate((isPassed) => {
				if (isPassed) {
					if (
						this.titleListModule.editedContentDetails.id &&
						this.titleListModule.editedContentDetails.id.length > 0 &&
						this.titleListModule.editeContentSelectedChapterId !== "none" &&
						this.titleListModule.editeContentSelectedChapterId !== "all"
					) {
						res({
							...this.contentDetails,
							chapterId: this.titleListModule.editeContentSelectedChapterId,
							chapterName: this.chapterName
						});
					} else {
						res(this.contentDetails);
					}
				} else {
					res(false);
				}
			});
		});
	}

	public selectControlContainer(element: HTMLDivElement) {
		return element.parentNode;
	}
}
